<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ batch ? batch.code : $t('kol.payout_batch_detail') || 'รายละเอียดรอบจ่ายเงิน' }}
            <span v-if="batch" :class="statusClass(batch.status)" class="ml-2">{{ batch.status }}</span>
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
          <div v-if="batch" class="text-white-50 small mt-1">
            {{ $t('kol.created_by') || 'โดย' }} {{ batch.created_by_name }}
            &nbsp;·&nbsp; {{ fmtDate(batch.created_at) }}
            <span v-if="batch.last_updated_at">
              &nbsp;·&nbsp; {{ $t('kol.payout_last_updated') || 'อัพเดทล่าสุด' }}: {{ fmtDate(batch.last_updated_at) }}
            </span>
          </div>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button
            v-if="batch && batch.status === 'draft' && canWriteKOL"
            size="md" type="neutral" class="mr-2"
            @click="submitBatch"
          >Submit</base-button>
          <base-button
            v-if="batch && (batch.status === 'draft' || batch.status === 'submitted') && canWriteKOL"
            size="md" type="neutral" class="mr-2"
            @click="cancelBatch"
          ><span class="text-danger">Cancel</span></base-button>
          <base-button size="md" type="neutral" @click="$router.push('/kol/payout-batches')">
            <i class="fa fa-arrow-left"></i> Back
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">

    <!-- Totals summary card -->
    <div class="row mt-2" v-if="batch">
      <div class="col-md-12">
        <card body-classes="py-3">
          <div class="row">
            <div class="col-md-2">
              <div class="text-muted small">Items</div>
              <div class="font-weight-bold">{{ batch.item_count || 0 }}</div>
            </div>
            <div class="col-md-3">
              <div class="text-muted small">{{ $t('kol.payout_progress') || 'ความคืบหน้า' }}</div>
              <div class="progress mt-1 mb-1" style="height:8px">
                <div class="progress-bar bg-success" :style="{ width: progressPct() + '%' }"></div>
              </div>
              <small>{{ batch.paid_count || 0 }} / {{ batch.item_count || 0 }} paid</small>
            </div>
            <div class="col-md-2 text-right">
              <div class="text-muted small">Gross</div>
              <div class="font-weight-bold">{{ fmt(batch.total_gross) }}</div>
            </div>
            <div class="col-md-2 text-right">
              <div class="text-muted small">WHT</div>
              <div class="font-weight-bold">{{ fmt(batch.total_wht) }}</div>
            </div>
            <div class="col-md-3 text-right">
              <div class="text-muted small">Net</div>
              <div class="font-weight-bold text-success" style="font-size:1.2em">{{ fmt(batch.total_net) }}</div>
            </div>
          </div>
        </card>
      </div>
    </div>

    <!-- Filter bar -->
    <div class="row mt-2">
      <div class="col-md-12">
        <card body-classes="py-3">
          <div class="row align-items-end">
            <div class="col-md-3">
              <label class="form-control-label small">{{ $t('kol.payout_filter_creator') || 'Creator' }}</label>
              <el-select v-model="filter.creator_id" clearable filterable :placeholder="$t('all') || 'All'" class="w-100" @change="loadDetail">
                <el-option v-for="c in creatorOptions" :key="c.id" :label="c.name" :value="c.id"></el-option>
              </el-select>
            </div>
            <div class="col-md-4">
              <label class="form-control-label small">{{ $t('kol.payout_filter_date_range') || 'ช่วงวันที่งาน' }}</label>
              <el-date-picker
                v-model="filter.dateRange"
                type="daterange"
                range-separator="-"
                start-placeholder="จาก"
                end-placeholder="ถึง"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                class="w-100"
                @change="loadDetail"
              ></el-date-picker>
            </div>
            <div class="col-md-3">
              <label class="form-control-label small">{{ $t('kol.payout_filter_payment_status') || 'สถานะการโอน' }}</label>
              <el-select v-model="filter.payment_status" clearable :placeholder="$t('all') || 'All'" class="w-100" @change="loadDetail">
                <el-option :label="$t('kol.payout_status_paid') || 'โอนแล้ว'" value="paid"></el-option>
                <el-option :label="$t('kol.payout_status_unpaid') || 'ยังไม่โอน'" value="unpaid"></el-option>
              </el-select>
            </div>
            <div class="col-md-2">
              <button class="btn btn-md btn-primary w-100" @click="loadDetail">
                <i class="fa fa-search"></i> ค้นหา
              </button>
            </div>
          </div>
        </card>
      </div>
    </div>

    <!-- Items table -->
    <div class="row mt-2">
      <div class="col-md-12">
        <card body-classes="px-0 pb-1">
          <el-table
            :data="items"
            v-loading="loading"
            stripe
            row-key="id"
            header-row-class-name="thead-light"
          >
            <!-- Assignment code + tax_type -->
            <el-table-column label="Assignment" min-width="150">
              <template slot-scope="{row}">
                <div class="text-primary font-weight-bold">{{ row.assignment_code || '-' }}</div>
                <span v-if="row.tax_type" class="badge badge-info text-uppercase mt-1">
                  {{ row.tax_type }}
                </span>
              </template>
            </el-table-column>

            <!-- Creator name (click to view PII) -->
            <el-table-column :label="$t('kol.creator_name') || 'Creator'" min-width="200">
              <template slot-scope="{row}">
                <a
                  v-if="row.creator_id"
                  href="#"
                  class="text-primary font-weight-bold"
                  @click.prevent="openCreatorPII(row)"
                  :title="$t('kol.payout_view_id_card') || 'บัตรประชาชน + Bookbank'"
                >
                  {{ row.creator_name || '-' }}
                  <i class="fa fa-id-card ml-1" style="font-size:11px"></i>
                </a>
                <span v-else>{{ row.creator_name || '-' }}</span>
              </template>
            </el-table-column>

            <!-- Bank -->
            <el-table-column label="Bank" min-width="180">
              <template slot-scope="{row}">
                <div>{{ row.bank_name || '-' }}</div>
                <small class="text-muted">{{ row.bank_account || '' }}</small>
              </template>
            </el-table-column>

            <!-- Gross / WHT / Net -->
            <el-table-column label="Gross" align="right" width="110">
              <template slot-scope="{row}">{{ fmt(row.gross_amount) }}</template>
            </el-table-column>
            <el-table-column label="WHT" align="right" width="100">
              <template slot-scope="{row}">{{ fmt(row.wht_amount) }}</template>
            </el-table-column>
            <el-table-column label="Net" align="right" width="110">
              <template slot-scope="{row}">{{ fmt(row.net_amount) }}</template>
            </el-table-column>

            <!-- Payment status -->
            <el-table-column :label="$t('kol.payout_filter_payment_status') || 'สถานะ'" width="120" align="center">
              <template slot-scope="{row}">
                <span :class="row.paid_at ? 'badge badge-success' : 'badge badge-secondary'">
                  {{ row.paid_at ? ($t('kol.payout_status_paid') || 'โอนแล้ว') : ($t('kol.payout_status_unpaid') || 'ยังไม่โอน') }}
                </span>
              </template>
            </el-table-column>

            <!-- Paid at + paid_by_name -->
            <el-table-column :label="$t('kol.payout_paid_at') || 'วันที่โอน'" min-width="160">
              <template slot-scope="{row}">
                <span v-if="row.paid_at">
                  <div>{{ fmtDate(row.paid_at) }}</div>
                  <small class="text-muted">{{ row.paid_by_name || '' }}</small>
                </span>
                <span v-else>-</span>
              </template>
            </el-table-column>

            <!-- Actions -->
            <el-table-column label="" min-width="220" align="right">
              <template slot-scope="{row}">
                <!-- บันทึกการโอน -->
                <button
                  v-if="!row.paid_at && canPayKOL"
                  class="btn btn-success btn-sm mb-1"
                  @click="openPayModal(row)"
                >{{ $t('kol.payout_pay_item') || 'บันทึกการโอน' }}</button>

                <!-- ดูสลิป -->
                <button
                  v-if="row.has_slip"
                  class="btn btn-outline-info btn-sm mb-1"
                  @click="viewItemSlip(row)"
                >{{ $t('kol.payout_view_slip') || 'ดูสลิป' }}</button>

                <!-- ยกเลิกการโอน -->
                <button
                  v-if="row.paid_at && canPayKOL"
                  class="btn btn-outline-danger btn-sm mb-1"
                  @click="unpayItem(row)"
                >{{ $t('kol.payout_unpay_item') || 'ยกเลิกการโอน' }}</button>

                <!-- เอกสารเพิ่มเติม -->
                <button
                  v-if="canPayKOL || canWriteKOL"
                  class="btn btn-outline-secondary btn-sm mb-1"
                  @click="openDocsDrawer(row)"
                >
                  {{ $t('kol.payout_documents') || 'เอกสารเพิ่มเติม' }}
                  <span v-if="row.document_count > 0" class="badge badge-info ml-1">{{ row.document_count }}</span>
                </button>
              </template>
            </el-table-column>
          </el-table>
        </card>
      </div>
    </div>

    <!-- Pay item modal -->
    <modal :show.sync="showPay" body-classes="p-0" modal-classes="modal-md">
      <card type="secondary" header-classes="bg-white pb-5" body-classes="px-lg-5 py-lg-5" class="border-0 mb-0">
        <h3>{{ $t('kol.payout_pay_item') || 'บันทึกการโอน' }}</h3>
        <p class="text-muted" v-if="payItem">{{ payItem.creator_name }} — {{ payItem.assignment_code }}</p>

        <div class="form-group">
          <label class="form-control-label">{{ $t('kol.payment_date') || 'วันที่ชำระ' }} <span class="text-danger">*</span></label>
          <input type="date" class="form-control" v-model="pay.date" />
        </div>

        <div class="form-group">
          <label class="form-control-label">Slip (image/PDF, max 10MB)</label>
          <input type="file" accept="image/*,application/pdf" @change="onPickPaySlip" />
          <div v-if="pay.fileName" class="text-muted small mt-1">
            <i class="fa fa-paperclip"></i> {{ pay.fileName }}
          </div>
        </div>

        <div class="form-group">
          <label class="form-control-label">{{ $t('kol.note') || 'หมายเหตุ' }} (optional)</label>
          <textarea class="form-control" rows="2" v-model="pay.note"></textarea>
        </div>

        <div class="text-right mt-3">
          <button class="btn btn-link" @click="showPay = false" :disabled="payLoading">Cancel</button>
          <button class="btn btn-success" :disabled="!pay.date || payLoading" @click="submitItemPay">
            <i class="fa fa-check"></i> {{ payLoading ? 'กำลังบันทึก...' : 'บันทึก' }}
          </button>
        </div>
      </card>
    </modal>

    <!-- Documents modal (per item) -->
    <modal :show.sync="showDocs" body-classes="p-0" modal-classes="modal-lg">
      <card type="secondary" header-classes="bg-white pb-5" body-classes="px-lg-5 py-lg-5" class="border-0 mb-0">
        <h3>{{ $t('kol.payout_documents') || 'เอกสารเพิ่มเติม' }}</h3>
        <p class="text-muted" v-if="docsItem">{{ docsItem.creator_name }} — {{ docsItem.assignment_code }}</p>

        <!-- Existing docs list -->
        <div v-if="docsLoading" class="text-center py-3">
          <i class="fa fa-spinner fa-spin"></i>
        </div>
        <div v-else>
          <div v-if="!docs.length" class="text-muted small mb-3">ยังไม่มีเอกสาร</div>
          <table v-else class="table table-sm mb-3">
            <thead>
              <tr>
                <th>ชื่อไฟล์</th>
                <th>อัพโหลดโดย</th>
                <th>วันที่</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="doc in docs" :key="doc.id">
                <td>{{ doc.filename }}</td>
                <td>{{ doc.uploaded_by_name || '-' }}</td>
                <td>{{ fmtDate(doc.uploaded_at) }}</td>
                <td class="text-right">
                  <button class="btn btn-link btn-sm p-0 mr-2" @click="downloadDoc(doc)">
                    <i class="fa fa-download"></i>
                  </button>
                  <button
                    v-if="canPayKOL"
                    class="btn btn-link btn-sm p-0 text-danger"
                    @click="deleteDoc(doc)"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Upload section -->
        <div v-if="canPayKOL || canWriteKOL" class="border-top pt-3">
          <label class="form-control-label">{{ $t('kol.payout_upload_doc') || 'อัพโหลดเอกสาร' }}</label>
          <div class="d-flex align-items-center">
            <input type="file" @change="onPickDocFile" class="mr-2" />
            <button class="btn btn-primary btn-sm" :disabled="!docUpload.file || docUpload.uploading" @click="submitDocUpload">
              <i class="fa fa-upload"></i> {{ docUpload.uploading ? 'กำลังอัพโหลด...' : 'Upload' }}
            </button>
          </div>
          <div v-if="docUpload.fileName" class="text-muted small mt-1">
            <i class="fa fa-paperclip"></i> {{ docUpload.fileName }}
          </div>
        </div>

        <div class="text-right mt-4">
          <button class="btn btn-link" @click="showDocs = false">Close</button>
        </div>
      </card>
    </modal>

    <!-- Creator PII Modal (ID Card + Bookbank) -->
    <modal :show.sync="showCreatorPII" modal-classes="modal-dialog-centered modal-xl modal-secondary" @close="onCreatorPIIClose">
      <h5 slot="header" class="modal-title">
        <i class="fa fa-id-card mr-1"></i>{{ creatorPII.name || 'Creator' }}
      </h5>
      <div class="row">
        <div class="col-md-6">
          <h6 class="text-uppercase text-muted small mb-2">{{ $t('kol.payout_view_id_card') || 'บัตรประชาชน' }}</h6>
          <div v-if="creatorPII.idCard.loading" class="text-center py-5">
            <i class="fa fa-spinner fa-spin fa-2x"></i>
          </div>
          <div v-else-if="!creatorPII.idCard.url" class="text-muted small py-3 text-center border rounded">
            ไม่พบไฟล์
          </div>
          <div v-else-if="isImageFile(creatorPII.idCard.url)" class="text-center">
            <img
              :src="creatorPII.idCard.url"
              alt="ID Card"
              style="max-width:100%; max-height:65vh; object-fit:contain;"
            />
          </div>
          <div v-else class="text-center py-3 border rounded">
            <button type="button" class="btn btn-md btn-primary" @click="openUrlNewTab(creatorPII.idCard.url)">
              <i class="fa fa-external-link-alt"></i> เปิดไฟล์
            </button>
          </div>
        </div>
        <div class="col-md-6">
          <h6 class="text-uppercase text-muted small mb-2">{{ $t('kol.payout_view_bookbank') || 'Bookbank' }}</h6>
          <div v-if="creatorPII.bookbank.loading" class="text-center py-5">
            <i class="fa fa-spinner fa-spin fa-2x"></i>
          </div>
          <div v-else-if="!creatorPII.bookbank.url" class="text-muted small py-3 text-center border rounded">
            ไม่พบไฟล์
          </div>
          <div v-else-if="isImageFile(creatorPII.bookbank.url)" class="text-center">
            <img
              :src="creatorPII.bookbank.url"
              alt="Bookbank"
              style="max-width:100%; max-height:65vh; object-fit:contain;"
            />
          </div>
          <div v-else class="text-center py-3 border rounded">
            <button type="button" class="btn btn-md btn-primary" @click="openUrlNewTab(creatorPII.bookbank.url)">
              <i class="fa fa-external-link-alt"></i> เปิดไฟล์
            </button>
          </div>
        </div>
      </div>
      <template slot="footer">
        <button type="button" class="btn btn-md btn-link ml-auto" @click="showCreatorPII = false">ปิด</button>
      </template>
    </modal>

    <!-- Image Preview Modal -->
    <modal :show.sync="showImagePreview" modal-classes="modal-dialog-centered modal-lg modal-secondary" @close="onImagePreviewClose">
      <h5 slot="header" class="modal-title">
        <i class="fa fa-image mr-1"></i>{{ imagePreviewName || 'Preview' }}
      </h5>
      <div class="text-center">
        <div v-if="imagePreviewLoading" class="py-5">
          <i class="fa fa-spinner fa-spin fa-2x"></i>
        </div>
        <img
          v-else-if="imagePreviewUrl"
          :src="imagePreviewUrl"
          :alt="imagePreviewName"
          style="max-width:100%; max-height:75vh; object-fit:contain;"
        />
      </div>
      <template slot="footer">
        <button
          v-if="imagePreviewUrl"
          type="button"
          class="btn btn-md btn-primary"
          @click="openImageInNewTab"
        >
          <i class="fa fa-external-link-alt"></i> เปิดในแท็บใหม่
        </button>
        <button type="button" class="btn btn-md btn-link ml-auto" @click="showImagePreview = false">
          ปิด
        </button>
      </template>
    </modal>

    </div><!-- /container-fluid -->
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, DatePicker } from "element-ui";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import KOLProvider from "@/resource/kol";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";

const kol = new KOLProvider();

export default {
  name: "KOLPayoutBatchDetail",
  components: {
    RouteBreadCrumb,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
  },
  data() {
    return {
      loading: false,
      batch: null,
      items: [],
      filter: {
        creator_id: null,
        dateRange: null,
        payment_status: null,
      },
      creatorOptions: [],
      // Pay item modal
      showPay: false,
      payItem: null,
      payLoading: false,
      pay: { file: null, fileName: "", date: "", note: "" },
      // Documents modal
      showDocs: false,
      docsItem: null,
      docsLoading: false,
      docs: [],
      docUpload: { file: null, fileName: "", uploading: false },
      // Image preview modal
      showImagePreview: false,
      imagePreviewUrl: "",
      imagePreviewName: "",
      imagePreviewLoading: false,
      // Creator PII modal
      showCreatorPII: false,
      creatorPII: {
        name: "",
        idCard: { loading: false, url: "" },
        bookbank: { loading: false, url: "" },
      },
    };
  },
  computed: {
    batchId() {
      return this.$route.params.id;
    },
    ...mapGetters({
      authUser: "Auth/getUser",
    }),
    roles() {
      return (this.authUser && this.authUser.roles) || {};
    },
    canWriteKOL() {
      return (this.roles.kol || 0) >= 2 || this.authUser.group === 99;
    },
    canPayKOL() {
      return (this.roles.kol_financial || 0) >= 2 || this.authUser.group === 99;
    },
  },
  mounted() {
    this.loadDetail();
  },
  methods: {
    fmt(v) {
      const n = Number(v || 0);
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtDate(d) { return d ? String(d).substring(0, 19).replace("T", " ") : "-"; },
    statusClass(s) {
      return {
        draft: "badge badge-secondary",
        submitted: "badge badge-info",
        paid: "badge badge-success",
        cancelled: "badge badge-warning",
      }[s] || "badge";
    },
    progressPct() {
      if (!this.batch || !this.batch.item_count) return 0;
      return Math.round(((this.batch.paid_count || 0) / this.batch.item_count) * 100);
    },
    buildQuery() {
      const q = {};
      if (this.filter.creator_id) q.creator_id = this.filter.creator_id;
      if (this.filter.payment_status) q.payment_status = this.filter.payment_status;
      if (this.filter.dateRange && this.filter.dateRange[0]) q.assignment_created_from = this.filter.dateRange[0];
      if (this.filter.dateRange && this.filter.dateRange[1]) q.assignment_created_to = this.filter.dateRange[1];
      return q;
    },
    async loadDetail() {
      this.loading = true;
      try {
        const res = await kol.PayoutBatchGet(this.batchId, this.buildQuery());
        const data = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.batch = data.batch || null;
        const rawItems = data.items || data.Items || [];
        this.items = rawItems;
        // Build creator options from distinct creators in the full list (reload without filter for dropdown)
        if (!this.creatorOptions.length && rawItems.length) {
          const seen = {};
          rawItems.forEach(item => {
            if (item.creator_id && !seen[item.creator_id]) {
              seen[item.creator_id] = true;
              this.creatorOptions.push({ id: item.creator_id, name: item.creator_name || String(item.creator_id) });
            }
          });
        }
      } catch (e) { console.log(e); }
      this.loading = false;
    },
    async submitBatch() {
      try {
        await kol.PayoutBatchSubmit(this.batchId);
        await this.loadDetail();
        swal.fire({ title: "Submitted", icon: "success", buttonsStyling: false, confirmButtonClass: "btn btn-success btn-fill" });
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
    },
    async cancelBatch() {
      const ok = await swal.fire({
        title: "Cancel batch?", icon: "warning", showCancelButton: true, buttonsStyling: false,
        confirmButtonText: "Yes",
        confirmButtonClass: "btn btn-danger btn-fill",
        cancelButtonClass: "btn btn-link",
      });
      if (!ok.value) return;
      try {
        await kol.PayoutBatchCancel(this.batchId);
        await this.loadDetail();
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
    },
    // ----- Image preview helpers -----
    isImageFile(name) {
      if (!name) return false;
      const ext = String(name).toLowerCase().split("?")[0].split("#")[0].split(".").pop();
      return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].indexOf(ext) >= 0;
    },
    showImageOrOpen(url, filename) {
      // If filename OR the URL path itself looks like an image, show in
      // modal; otherwise open in a new tab. The URL fallback matters
      // because presigned S3 URLs end in `/<unix>-<original>.<ext>` —
      // when the caller doesn't have a separate filename, the URL still
      // carries enough to detect images.
      if (this.isImageFile(filename) || this.isImageFile(url)) {
        this.imagePreviewName = filename || "Preview";
        this.imagePreviewUrl = url;
        this.imagePreviewLoading = false;
        this.showImagePreview = true;
      } else {
        window.open(url, "_blank");
      }
    },
    openUrlNewTab(url) {
      if (url) window.open(url, "_blank");
    },
    onImagePreviewClose() {
      this.imagePreviewUrl = "";
      this.imagePreviewName = "";
    },
    openImageInNewTab() {
      if (this.imagePreviewUrl) window.open(this.imagePreviewUrl, "_blank");
    },
    // ----- Creator PII modal (ID Card + Bookbank side-by-side) -----
    async openCreatorPII(row) {
      if (!row || !row.creator_id) return;
      this.creatorPII = {
        name: row.creator_name || "",
        idCard: { loading: true, url: "" },
        bookbank: { loading: true, url: "" },
      };
      this.showCreatorPII = true;

      const fetch = async (type) => {
        try {
          const res = await kol.CreatorGetFileURL(row.creator_id, type);
          const data = (res.data && res.data.Data && res.data.Data[0]) || {};
          return data.url || "";
        } catch (_) { return ""; }
      };

      const [idCardUrl, bookbankUrl] = await Promise.all([
        fetch("id_card"),
        fetch("bookbank"),
      ]);
      this.creatorPII.idCard = { loading: false, url: idCardUrl };
      this.creatorPII.bookbank = { loading: false, url: bookbankUrl };
    },
    onCreatorPIIClose() {
      this.creatorPII = {
        name: "",
        idCard: { loading: false, url: "" },
        bookbank: { loading: false, url: "" },
      };
    },
    // ----- Pay item -----
    openPayModal(row) {
      this.payItem = row;
      this.pay = { file: null, fileName: "", date: new Date().toISOString().slice(0, 10), note: "" };
      this.showPay = true;
    },
    onPickPaySlip(ev) {
      const f = ev.target.files && ev.target.files[0];
      if (!f) return;
      if (f.size > 10 * 1024 * 1024) {
        swal.fire({ title: "ไฟล์ต้องไม่เกิน 10MB", icon: "warning", buttonsStyling: false, confirmButtonClass: "btn btn-warning btn-fill" });
        return;
      }
      this.pay.file = f;
      this.pay.fileName = f.name;
    },
    async submitItemPay() {
      if (!this.pay.date) return;
      this.payLoading = true;
      try {
        const fd = new FormData();
        fd.append("payment_date", this.pay.date);
        if (this.pay.file) fd.append("slip", this.pay.file);
        if (this.pay.note) fd.append("payment_note", this.pay.note);
        await kol.PayoutBatchItemPay(this.batchId, this.payItem.id, fd);
        this.showPay = false;
        await this.loadDetail();
        swal.fire({ title: "บันทึกการโอนสำเร็จ", icon: "success", buttonsStyling: false, confirmButtonClass: "btn btn-success btn-fill" });
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
      this.payLoading = false;
    },
    // ----- View item slip -----
    async viewItemSlip(row) {
      try {
        const res = await kol.PayoutBatchItemSlipURL(this.batchId, row.id);
        const data = (res.data && res.data.Data && res.data.Data[0]) || {};
        if (data.url) this.showImageOrOpen(data.url, data.filename || "slip");
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
    },
    // ----- Unpay item -----
    async unpayItem(row) {
      const ok = await swal.fire({
        title: "ยกเลิกการโอน?", icon: "warning", showCancelButton: true, buttonsStyling: false,
        confirmButtonText: "Yes",
        confirmButtonClass: "btn btn-danger btn-fill",
        cancelButtonClass: "btn btn-link",
      });
      if (!ok.value) return;
      try {
        await kol.PayoutBatchItemUnpay(this.batchId, row.id, {});
        await this.loadDetail();
        swal.fire({ title: "ยกเลิกการโอนสำเร็จ", icon: "success", buttonsStyling: false, confirmButtonClass: "btn btn-success btn-fill" });
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
    },
    // ----- Documents drawer -----
    async openDocsDrawer(row) {
      this.docsItem = row;
      this.docs = [];
      this.docUpload = { file: null, fileName: "", uploading: false };
      this.showDocs = true;
      this.docsLoading = true;
      try {
        const res = await kol.PayoutBatchItemDocList(this.batchId, row.id);
        const data = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.docs = data.items || data.Items || [];
      } catch (e) { console.log(e); }
      this.docsLoading = false;
    },
    async downloadDoc(doc) {
      try {
        const res = await kol.PayoutBatchItemDocURL(this.batchId, this.docsItem.id, doc.id);
        const data = (res.data && res.data.Data && res.data.Data[0]) || {};
        if (data.url) this.showImageOrOpen(data.url, doc.filename || data.filename);
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
    },
    async deleteDoc(doc) {
      const ok = await swal.fire({
        title: "ลบเอกสาร?", icon: "warning", showCancelButton: true, buttonsStyling: false,
        confirmButtonText: "ลบ",
        confirmButtonClass: "btn btn-danger btn-fill",
        cancelButtonClass: "btn btn-link",
      });
      if (!ok.value) return;
      try {
        await kol.PayoutBatchItemDocDelete(this.batchId, this.docsItem.id, doc.id);
        this.docs = this.docs.filter(d => d.id !== doc.id);
        // Decrement count badge in the items table
        const item = this.items.find(i => i.id === this.docsItem.id);
        if (item && item.document_count > 0) item.document_count -= 1;
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
    },
    onPickDocFile(ev) {
      const f = ev.target.files && ev.target.files[0];
      if (!f) return;
      this.docUpload.file = f;
      this.docUpload.fileName = f.name;
    },
    async submitDocUpload() {
      if (!this.docUpload.file) return;
      this.docUpload.uploading = true;
      try {
        const fd = new FormData();
        fd.append("file", this.docUpload.file);
        await kol.PayoutBatchItemDocUpload(this.batchId, this.docsItem.id, fd);
        // Reload docs list
        const res = await kol.PayoutBatchItemDocList(this.batchId, this.docsItem.id);
        const data = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.docs = data.items || data.Items || [];
        this.docUpload = { file: null, fileName: "", uploading: false };
        // Increment count badge in items table
        const item = this.items.find(i => i.id === this.docsItem.id);
        if (item) item.document_count = this.docs.length;
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
      this.docUpload.uploading = false;
    },
  },
};
</script>
